/* ============================================
   READING PROGRESS COMPONENT
   The Creative Orchestra - GEMINI Framework
   Reading progress indicators and tracking
   ============================================ */

import { useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BookOpen, Clock, CheckCircle2 } from 'lucide-react';
import useReadingProgress from '@hooks/useReadingProgress';
import useBookmark from '@hooks/useBookmark';
import { chapterList } from '@data';

/**
 * Reading Progress Bar
 * Fixed position progress bar at top of page
 * 
 * @param {Object} props
 * @param {number} props.height - Bar height in pixels
 * @param {string} props.color - Bar color
 * @param {boolean} props.showPercentage - Show percentage label
 */
const ReadingProgressBar = ({
  height = 3,
  color = '#818CF8',
  showPercentage = false,
  className = '',
}) => {
  const { progress, isScrolling } = useReadingProgress();

  return (
    <div 
      className={`fixed top-0 left-0 right-0 z-50 ${className}`}
      style={{ height }}
    >
      <motion.div
        className="h-full"
        style={{ 
          width: `${progress}%`,
          background: `linear-gradient(90deg, ${color}, ${color}80)`,
        }}
        initial={false}
        animate={{ width: `${progress}%` }}
        transition={{ 
          duration: isScrolling ? 0.05 : 0.3,
          ease: 'linear',
        }}
      />

      {/* Percentage label */}
      <AnimatePresence>
        {showPercentage && progress > 0 && progress < 100 && (
          <motion.span
            className="absolute right-4 top-4 text-xs text-gray-500"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {Math.round(progress)}%
          </motion.span>
        )}
      </AnimatePresence>
    </div>
  );
};

/**
 * Chapter Progress Indicator
 * Shows progress through chapters
 */
export const ChapterProgressIndicator = ({
  currentChapterId,
  className = '',
}) => {
  const { visitedChapters, getReadingStats } = useBookmark();
  const { progress } = useReadingProgress();

  const currentIndex = chapterList.findIndex(ch => ch.id === currentChapterId);
  const stats = useMemo(() => 
    getReadingStats(chapterList.length), [getReadingStats, chapterList.length]
  );

  return (
    <div className={`${className}`}>
      {/* Chapter position */}
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm text-gray-400">
          Chapter {currentIndex + 1} of {chapterList.length}
        </span>
        <span className="text-sm text-gray-500">
          {Math.round(progress)}% through chapter
        </span>
      </div>

      {/* Progress bar */}
      <div className="relative h-1 bg-white/10 rounded-full overflow-hidden">
        {/* Chapter markers */}
        {chapterList.map((_, index) => (
          <div
            key={index}
            className="absolute top-0 bottom-0 w-px bg-white/20"
            style={{ left: `${((index + 1) / chapterList.length) * 100}%` }}
          />
        ))}

        {/* Overall progress */}
        <motion.div
          className="absolute top-0 bottom-0 bg-indigo-500"
          style={{ 
            width: `${((currentIndex + progress / 100) / chapterList.length) * 100}%`,
          }}
          initial={false}
          animate={{ 
            width: `${((currentIndex + progress / 100) / chapterList.length) * 100}%`,
          }}
          transition={{ duration: 0.3 }}
        />

        {/* Current position marker */}
        <motion.div
          className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full 
            bg-white border-2 border-indigo-500"
          style={{
            left: `${((currentIndex + progress / 100) / chapterList.length) * 100}%`,
            marginLeft: -6,
          }}
          initial={false}
          animate={{
            left: `${((currentIndex + progress / 100) / chapterList.length) * 100}%`,
          }}
          transition={{ duration: 0.3 }}
        />
      </div>

      {/* Stats */}
      <div className="flex items-center justify-between mt-2 text-xs text-gray-500">
        <span>{stats.chaptersRead} chapters read</span>
        <span>{stats.remainingChapters} remaining</span>
      </div>
    </div>
  );
};

/**
 * Reading Stats Card
 * Displays overall reading statistics
 */
export const ReadingStatsCard = ({ className = '' }) => {
  const { visitedChapters, bookmark, getReadingStats, getBookmarkDisplay } = useBookmark();

  const stats = getReadingStats(chapterList.length);
  const bookmarkDisplay = getBookmarkDisplay();

  // Calculate total reading time
  const totalReadingTime = useMemo(() => {
    const totalMinutes = chapterList.reduce((acc, ch) => {
      const minutes = parseInt(ch.readingTime) || 15;
      return acc + minutes;
    }, 0);
    const hours = Math.floor(totalMinutes / 60);
    const mins = totalMinutes % 60;
    return `${hours}h ${mins}m`;
  }, []);

  const completedReadingTime = useMemo(() => {
    const completedMinutes = visitedChapters.reduce((acc, chId) => {
      const chapter = chapterList.find(ch => ch.id === chId);
      const minutes = parseInt(chapter?.readingTime) || 15;
      return acc + minutes;
    }, 0);
    const hours = Math.floor(completedMinutes / 60);
    const mins = completedMinutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  }, [visitedChapters]);

  return (
    <div className={`p-6 rounded-xl bg-white/5 border border-white/10 ${className}`}>
      <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
        <BookOpen className="w-5 h-5 text-indigo-400" />
        Reading Progress
      </h3>

      {/* Progress circle */}
      <div className="flex items-center gap-6 mb-6">
        <ProgressCircle percentage={stats.percentage} size={80} />
        <div>
          <p className="text-3xl font-bold text-white">
            {stats.percentage}%
          </p>
          <p className="text-sm text-gray-400">
            {stats.chaptersRead} of {stats.totalChapters} chapters
          </p>
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="p-3 rounded-lg bg-white/5">
          <div className="flex items-center gap-2 text-gray-400 mb-1">
            <Clock className="w-4 h-4" />
            <span className="text-xs">Time spent</span>
          </div>
          <p className="text-lg font-semibold text-white">{completedReadingTime}</p>
        </div>
        <div className="p-3 rounded-lg bg-white/5">
          <div className="flex items-center gap-2 text-gray-400 mb-1">
            <Clock className="w-4 h-4" />
            <span className="text-xs">Remaining</span>
          </div>
          <p className="text-lg font-semibold text-white">{totalReadingTime}</p>
        </div>
      </div>

      {/* Last bookmark */}
      {bookmarkDisplay && (
        <div className="p-3 rounded-lg bg-indigo-500/10 border border-indigo-500/20">
          <p className="text-xs text-indigo-400 mb-1">Last read</p>
          <p className="text-sm text-white font-medium truncate">
            {bookmarkDisplay.chapterTitle}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            {bookmarkDisplay.progress} • {bookmarkDisplay.timeAgo}
          </p>
        </div>
      )}

      {/* Completion badge */}
      {stats.isComplete && (
        <div className="mt-4 p-3 rounded-lg bg-green-500/10 border border-green-500/20
          flex items-center gap-2">
          <CheckCircle2 className="w-5 h-5 text-green-500" />
          <span className="text-sm text-green-400 font-medium">
            Completed!
          </span>
        </div>
      )}
    </div>
  );
};

/**
 * Progress Circle Component
 */
const ProgressCircle = ({ percentage, size = 60 }) => {
  const strokeWidth = 6;
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  return (
    <svg width={size} height={size} className="transform -rotate-90">
      {/* Background circle */}
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="rgba(255,255,255,0.1)"
        strokeWidth={strokeWidth}
      />
      {/* Progress circle */}
      <motion.circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="url(#progressGradient)"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeDasharray={circumference}
        initial={{ strokeDashoffset: circumference }}
        animate={{ strokeDashoffset }}
        transition={{ duration: 1, ease: 'easeOut' }}
      />
      {/* Gradient definition */}
      <defs>
        <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#818CF8" />
          <stop offset="100%" stopColor="#A78BFA" />
        </linearGradient>
      </defs>
    </svg>
  );
};

/**
 * Inline Progress Text
 */
export const InlineProgress = ({ currentChapterId, className = '' }) => {
  const { progress } = useReadingProgress();
  const currentIndex = chapterList.findIndex(ch => ch.id === currentChapterId);

  return (
    <span className={`text-sm text-gray-500 ${className}`}>
      Chapter {currentIndex + 1}/{chapterList.length} • {Math.round(progress)}%
    </span>
  );
};

/**
 * Mini Progress Bar (for compact displays)
 */
export const MiniProgressBar = ({ className = '' }) => {
  const { progress } = useReadingProgress();

  return (
    <div className={`h-1 bg-white/10 rounded-full overflow-hidden ${className}`}>
      <motion.div
        className="h-full bg-indigo-500"
        style={{ width: `${progress}%` }}
        initial={false}
        animate={{ width: `${progress}%` }}
        transition={{ duration: 0.1 }}
      />
    </div>
  );
};

export default ReadingProgressBar;
